import React, { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  discordComposerService,
  MAX_FILE_BYTES,
  MAX_TOTAL_UPLOAD_BYTES,
  formatBytes,
} from '../services/discordComposerService';
import { RecentEmojisBar, EmojiPickerModal } from './EmojiPicker';

interface DiscordComposerWidgetProps {
  onRemove: () => void;
}

export const DiscordComposerWidget: React.FC<DiscordComposerWidgetProps> = ({ onRemove }) => {
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Fetch webhook groups
  const { data: webhookGroups, isLoading: groupsLoading } = useQuery({
    queryKey: ['discord-composer', 'webhook-groups'],
    queryFn: () => discordComposerService.getWebhookGroups(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch bot identities
  const { data: botIdentities } = useQuery({
    queryKey: ['discord-composer', 'bot-identities'],
    queryFn: () => discordComposerService.getBotIdentities(),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  // Convert File[] to FileList-like object for the service
  const createFileList = (filesArray: File[]): FileList | null => {
    if (filesArray.length === 0) return null;
    const dt = new DataTransfer();
    filesArray.forEach(file => dt.items.add(file));
    return dt.files;
  };

  // Send mutation
  const sendMutation = useMutation({
    mutationFn: () => {
      if (!selectedGroupId) throw new Error('No webhook group selected');
      return discordComposerService.sendMessage(selectedGroupId, content, createFileList(files));
    },
    onSuccess: (response) => {
      // Check response status and data
      const isSuccess = response.status === 'success' && response.data?.any_success;

      if (isSuccess) {
        setStatus('success');
        const channelCount = response.data.successful_sends || response.data.channels?.length || 0;
        const channelNames = response.data.channels?.join(', ') || '';
        setStatusMessage(
          channelCount > 0
            ? `Sent to ${channelCount} channel${channelCount !== 1 ? 's' : ''}${channelNames ? `: ${channelNames}` : ''}`
            : 'Message sent!'
        );
        setContent('');
        setFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setTimeout(() => setStatus('idle'), 3000);
      } else {
        // Some or all sends failed
        setStatus('error');
        const failedChannels = response.data?.results
          ?.filter(r => !r.success)
          ?.map(r => r.channel_name)
          ?.join(', ');
        setStatusMessage(
          response.error ||
          (failedChannels ? `Failed to send to: ${failedChannels}` : 'Failed to send')
        );
        setTimeout(() => setStatus('idle'), 5000);
      }
    },
    onError: (error: Error) => {
      setStatus('error');
      setStatusMessage(error.message || 'Failed to send message');
      setTimeout(() => setStatus('idle'), 5000);
    },
  });

  // Add files helper (dedupes by name, enforces the upload budget)
  //
  // Every entry path — the file input, paste, and drag-drop — funnels through
  // here, so this is the only place the size guard has to live. Oversized
  // attachments used to reach nginx and come back as an opaque 413 with no clue
  // which file was at fault.
  const addFiles = useCallback((newFiles: File[]) => {
    // The budget is computed against `prev` inside the updater so concurrent
    // adds can't race a stale total. The updater itself must stay pure (React
    // may run it twice), so the rejection message is dispatched outside it.
    setFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      const uniqueNew = newFiles.filter(f => !existingNames.has(f.name));

      const rejected: string[] = [];
      let total = prev.reduce((sum, f) => sum + f.size, 0);
      const admitted: File[] = [];

      for (const file of uniqueNew) {
        if (file.size > MAX_FILE_BYTES) {
          rejected.push(
            `${file.name} (${formatBytes(file.size)}) exceeds the ${formatBytes(MAX_FILE_BYTES)} per-file limit`,
          );
          continue;
        }
        // Admit while the running total still fits, so the user keeps whatever
        // they added first rather than losing the whole batch.
        if (total + file.size > MAX_TOTAL_UPLOAD_BYTES) {
          rejected.push(
            `${file.name} (${formatBytes(file.size)}) would exceed the ${formatBytes(MAX_TOTAL_UPLOAD_BYTES)} total upload limit`,
          );
          continue;
        }
        total += file.size;
        admitted.push(file);
      }

      if (rejected.length > 0) {
        // Deferred so the state update happens outside this updater.
        queueMicrotask(() => {
          setStatus('error');
          setStatusMessage(rejected.join('; '));
        });
      }

      return admitted.length > 0 ? [...prev, ...admitted] : prev;
    });
  }, []);

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
    }
  };

  // Handle paste (for images)
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          // Generate a unique name for pasted images
          const ext = item.type.split('/')[1] || 'png';
          const timestamp = Date.now();
          const namedFile = new File([file], `pasted-image-${timestamp}.${ext}`, { type: file.type });
          imageFiles.push(namedFile);
        }
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault(); // Prevent default paste if we handled images
      addFiles(imageFiles);
    }
  }, [addFiles]);

  // Handle drag events
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging false if we're leaving the drop zone entirely
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      addFiles(Array.from(droppedFiles));
    }
  }, [addFiles]);

  // Remove a specific file
  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearFiles = () => {
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Insert emoji at cursor position
  const insertEmoji = useCallback((emoji: string) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = content.slice(0, start) + emoji + content.slice(end);
      if (newContent.length <= 2000) {
        setContent(newContent);
        // Reset cursor position after emoji
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
          textarea.focus();
        }, 0);
      }
    } else {
      // No cursor position, append to end
      if (content.length + emoji.length <= 2000) {
        setContent(content + emoji);
      }
    }
  }, [content]);

  const handleSend = () => {
    if (!selectedGroupId || !content.trim()) return;
    sendMutation.mutate();
  };

  const canSend = selectedGroupId && content.trim() && !sendMutation.isPending;

  return (
    <div
      ref={dropZoneRef}
      className={`h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden relative ${
        isDragging ? 'ring-2 ring-[#5865F2] ring-inset' : ''
      }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onPaste={handlePaste}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-[#5865F2]/20 z-10 flex items-center justify-center pointer-events-none">
          <div className="bg-dark-surface px-4 py-2 rounded-lg border border-[#5865F2] text-[#5865F2] text-sm font-medium">
            Drop files here
          </div>
        </div>
      )}

      {/* Title Bar - drag handle */}
      <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
          </svg>
          <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Discord Composer</h3>
        </div>
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-0.5 rounded transition-colors"
          title="Close widget"
        >
          ×
        </button>
      </div>

      {/* Content - Two column layout */}
      <div className="flex-1 flex gap-3 p-3 overflow-hidden">
        {/* Left Column - Channel Groups */}
        <div className="flex flex-col gap-1 min-w-0" style={{ width: '120px', flexShrink: 0 }}>
          <label className="text-xs text-gray-400 mb-0.5">Channel</label>
          {groupsLoading ? (
            <div className="text-xs text-gray-500">Loading...</div>
          ) : webhookGroups && webhookGroups.length > 0 ? (
            <div className="flex flex-col gap-1 overflow-y-auto">
              {(() => {
                const sections = new Map<string, typeof webhookGroups>();
                for (const group of webhookGroups) {
                  const section = group.section || 'Other';
                  if (!sections.has(section)) sections.set(section, []);
                  sections.get(section)!.push(group);
                }
                return Array.from(sections.entries()).map(([section, groups]) => (
                  <div key={section}>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-1 mb-0.5 px-1 truncate">
                      {section}
                    </div>
                    {groups.map((group) => (
                      <button
                        key={group.id}
                        onClick={() => setSelectedGroupId(group.id)}
                        className={`w-full text-left text-xs px-2 py-1 rounded truncate transition-colors ${
                          selectedGroupId === group.id
                            ? 'bg-[#5865F2] text-white'
                            : 'text-gray-300 hover:bg-dark-surface-light'
                        }`}
                        title={group.description || group.display_name}
                      >
                        {group.display_name}
                      </button>
                    ))}
                  </div>
                ));
              })()}
            </div>
          ) : (
            <div className="text-xs text-gray-500">No channels</div>
          )}
        </div>

        {/* Right Column - Message & Actions */}
        <div className="flex-1 flex flex-col gap-2 min-w-0">
          {/* Message textarea */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-gray-400">Message</label>
              <span className={`text-xs ${content.length > 1900 ? 'text-accent-yellow' : 'text-gray-500'}`}>
                {content.length}/2000
              </span>
            </div>
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value.slice(0, 2000))}
              placeholder="Enter your message... (paste images with Ctrl+V)"
              className="flex-1 w-full px-2 py-1.5 bg-dark-surface border border-dark-border rounded text-sm text-gray-200 resize-none focus:outline-none focus:border-accent-yellow"
            />
            {/* Inline recent emojis */}
            <div className="mt-1">
              <RecentEmojisBar
                onSelect={insertEmoji}
                onOpenPicker={() => setShowEmojiPicker(true)}
              />
            </div>
          </div>

          {/* Emoji Picker Modal */}
          {showEmojiPicker && (
            <EmojiPickerModal
              onSelect={(emoji) => {
                insertEmoji(emoji);
              }}
              onClose={() => setShowEmojiPicker(false)}
            />
          )}

          {/* File attachments */}
          <div>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="discord-file-input"
                accept="image/*,video/*,audio/*,.pdf,.txt,.json,.csv,.md"
              />
              <label
                htmlFor="discord-file-input"
                className="flex items-center gap-1 px-2 py-1 bg-dark-bg border border-dark-border rounded text-xs text-gray-300 hover:bg-dark-surface-light cursor-pointer transition-colors"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                Attach
              </label>
              {files.length > 0 && (
                <>
                  <span className="text-xs text-gray-400">{files.length} file{files.length !== 1 ? 's' : ''}</span>
                  <button
                    onClick={handleClearFiles}
                    className="text-xs text-gray-500 hover:text-bear-red transition-colors"
                  >
                    Clear
                  </button>
                </>
              )}
            </div>

            {/* File list */}
            {files.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {files.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center gap-1 px-1.5 py-0.5 bg-dark-bg rounded text-xs text-gray-300 border border-dark-border"
                  >
                    {file.type.startsWith('image/') && (
                      <img
                        src={URL.createObjectURL(file)}
                        alt=""
                        className="w-3 h-3 object-cover rounded"
                      />
                    )}
                    <span className="max-w-[80px] truncate">{file.name}</span>
                    <button
                      onClick={() => removeFile(index)}
                      className="text-gray-500 hover:text-bear-red"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status message */}
          {status !== 'idle' && (
            <div
              className={`text-xs px-2 py-1 rounded ${
                status === 'success'
                  ? 'bg-bull-blue/20 text-bull-blue'
                  : 'bg-bear-red/20 text-bear-red'
              }`}
            >
              {statusMessage}
            </div>
          )}

          {/* Posting as indicator */}
          {botIdentities && botIdentities.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span>Posting as</span>
              {botIdentities[0].avatar_url ? (
                <img src={botIdentities[0].avatar_url} alt="" className="w-4 h-4 rounded-full" />
              ) : null}
              <span className="text-gray-300">{botIdentities[0].name}</span>
            </div>
          )}

          {/* Send button */}
          <button
            onClick={handleSend}
            disabled={!canSend}
            className={`w-full py-2 rounded font-medium text-sm transition-colors ${
              canSend
                ? 'bg-[#5865F2] hover:bg-[#4752C4] text-white'
                : 'bg-dark-border text-gray-500 cursor-not-allowed'
            }`}
          >
            {sendMutation.isPending ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Sending...
              </span>
            ) : (
              'Send to Discord'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
